import { useEffect, useMemo, useState } from 'react';

import { Flag02Icon } from '@hugeicons-pro/core-solid-rounded';
import { File01Icon, TaskEdit01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { FormResponse } from 'components/Form/FormResponse/FormResponse';
import { Button } from 'components/FormComponents/Button/Button';
import { IconComponent } from 'components/Icon/IconComponent';
import { ItemOverviewCard } from 'components/ItemOverviewCard/ItemOverviewCard';
import { List } from 'components/List/List';
import { SummaryStat } from 'components/SummaryStat/SummaryStat';

import { useLazyGetCandidateCheckResultQuery } from 'api/candidate-checks-api';
import { useLazyGetFormAnswersForCandidateQuery, useLazyGetFormsByIdQuery } from 'api/forms-api';
import { useLazyGetSubCheckResultQuery } from 'api/sub-checks-api';

export const Questionnaire = ({
  checks,
  candidate,
  brand,
  company,
  canAccessResults = true,
  setLoading = () => {},
}) => {
  const { t } = useTranslation();

  const [getFormsById] = useLazyGetFormsByIdQuery();
  const [getFormAnswersForCandidate] = useLazyGetFormAnswersForCandidateQuery();
  const [getCheckReport] = useLazyGetCandidateCheckResultQuery();
  const [getSubCheckResult] = useLazyGetSubCheckResultQuery();

  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState();
  const [selectedForm, setSelectedForm] = useState();
  const [answers, setAnswers] = useState([]);
  const [flagSeverities, setFlagSeverities] = useState({});

  const hasSubChecks = useMemo(() => checks.some(check => check.sub_checks?.length > 0), [checks]);

  const allSubChecks = useMemo(() => checks.flatMap(check => check.sub_checks ?? []), [checks]);

  const subCheckByFormId = useMemo(
    () => Object.fromEntries(allSubChecks.map(sc => [sc.details.id, sc])),
    [allSubChecks],
  );

  const parentCheckByFormId = useMemo(
    () =>
      Object.fromEntries(
        checks.flatMap(check => (check.sub_checks ?? []).map(sc => [sc.details.id, check])),
      ),
    [checks],
  );

  const checkByFormId = useMemo(
    () => Object.fromEntries(checks.map(check => [check.details.form_id, check])),
    [checks],
  );

  const getMeta = formId => (hasSubChecks ? subCheckByFormId[formId] : checkByFormId[formId]);

  useEffect(() => {
    setLoading(true);

    const formIds = hasSubChecks
      ? allSubChecks.map(sc => sc.details.id)
      : checks.map(check => check.details.form_id);

    getFormsById(formIds)
      .unwrap()
      .then(forms => {
        setForms(
          forms.map(form => ({
            ...form,
            completed: getMeta(form.id)?.status === 'completed',
          })),
        );
        setLoading(false);
      });
  }, [checks]);

  useEffect(() => {
    if (!selectedFormId) return;

    setLoading(true);
    getFormAnswersForCandidate({ id: selectedFormId, candidateId: candidate.id })
      .unwrap()
      .then(data => {
        setAnswers(data.answers ?? data);
        setFlagSeverities(data.flag_severities ?? {});
        setSelectedForm(forms.find(form => form.id == selectedFormId));
        setLoading(false);
      });
  }, [selectedFormId]);

  const download = async () => {
    setLoading(true);
    if (hasSubChecks) {
      await getSubCheckResult({
        subCheck: subCheckByFormId[selectedFormId],
        check: parentCheckByFormId[selectedFormId],
        candidate,
      }).unwrap();
    } else {
      await getCheckReport({
        check: checkByFormId[selectedFormId],
        candidate,
      }).unwrap();
    }
    setLoading(false);
  };

  const flags = useMemo(
    () =>
      Object.values(flagSeverities || {}).reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {}),
    [flagSeverities],
  );

  const severityCount = useMemo(() => Object.keys(flagSeverities).length, [flagSeverities]);

  const submittedAt = selectedFormId ? getMeta(selectedFormId)?.submitted_at : null;

  return (
    <div className="flex gap-4">
      <div className="bg-neutral-2 rounded-3xl border border-neutral-4 shadow-sm flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-4 bg-neutral-1 border-b border-neutral-4">
          <p className="text-xl font-medium text-neutral-7 uppercase tracking-wide">
            Questionnaires
          </p>
        </div>
        <div className="flex flex-col gap-4 p-4">
          {(forms || []).map(form => (
            <ItemOverviewCard
              key={form.id}
              category={{ icon: TaskEdit01Icon, text: 'Questionnaire' }}
              title={form.name}
              description={form.description}
              tags={[
                {
                  label: form.completed ? 'Completed' : 'Not Completed',
                  type: form.completed ? 'success' : 'neutral',
                },
              ]}
              onClick={() => (form.completed ? setSelectedFormId(form.id) : () => {})}
              disabled={!form.completed}
              className={selectedFormId === form.id ? 'border-primary-4' : ''}
            />
          ))}
        </div>
      </div>
      {selectedForm && (
        <div className="border border-neutral-4 rounded-3xl border border-neutral-4 shadow-sm bg-neutral-1 w-1/2 overflow-hidden">
          <div className="p-8 flex flex-col gap-8">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <IconComponent icon={File01Icon} />
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-medium text-text-tertiary uppercase tracking-wide">
                    Form
                  </p>
                  <h1 className="text-4xl font-medium tracking-tight text-neutral-7">
                    {selectedForm?.name}
                  </h1>
                </div>
              </div>
              {canAccessResults && (
                <Button
                  type="secondary"
                  small
                  light
                  icon="download"
                  tip={t('common.download')}
                  onClick={download}>
                  Download
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <SummaryStat
                label="Submitted"
                value={submittedAt ? moment(submittedAt).format('MMMM D, YYYY') : '—'}
              />
              <SummaryStat label="Total Questions" value={Object.keys(answers)?.length} />
              <SummaryStat
                label="Flagged Responses"
                value={severityCount > 0 ? severityCount : '-'}
                {...(severityCount > 0
                  ? {
                      breakdown: [
                        { count: flags?.high ?? 0, className: 'text-red-2' },
                        { count: flags?.medium ?? 0, className: 'text-orange-2' },
                        { count: flags?.low ?? 0, className: 'text-blue-3' },
                      ],
                      breakdownIcon: <HugeiconsIcon icon={Flag02Icon} size={20} />,
                    }
                  : {})}
              />
            </div>
          </div>
          <div className="border-b border-neutral-4" />
          <FormResponse
            form={selectedForm}
            answers={answers}
            flagSeverities={flagSeverities}
            params={{
              candidate: candidate.first_name,
              candidateFirstName: candidate.first_name,
              candidateFullName: candidate.name,
              candidateLastName: candidate.last_name,
              job: candidate.job_role,
              companyName: brand?.brand_name || company?.name,
            }}
          />
        </div>
      )}
    </div>
  );
};
